import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { collectionsApi } from '@/lib/api';
import { db } from '@/lib/db';
import { useWorkspaceStore, selectActiveWorkspace, selectCollectionsList } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import AppShell from '@/components/layout/AppShell';
import type { Collection } from '@/types';

// ─── Collection card ──────────────────────────────────────────────────────────

const CollectionCard: React.FC<{
  collection: Collection;
  capabilityCount: number;
  onClick: () => void;
}> = ({ collection, capabilityCount, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full text-left bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-card hover:shadow-card-hover hover:border-primary-300 dark:hover:border-primary-700 transition-all overflow-hidden"
  >
    {/* Cover / header */}
    <div
      className="h-16 bg-gradient-to-br from-primary-700 to-primary-500 flex items-end p-3"
      style={collection.coverUrl ? { backgroundImage: `url(${collection.coverUrl})`, backgroundSize: 'cover' } : {}}
    >
      <svg className="w-5 h-5 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M2 8.5h20M2 8.5V6a2 2 0 0 1 2-2h4.5l2 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.5z"/></svg>
    </div>

    <div className="p-4">
      <h3 className="font-semibold text-surface-900 dark:text-white text-sm leading-tight">
        {collection.name}
      </h3>
      {collection.description && (
        <p className="text-xs text-surface-500 mt-1 line-clamp-2">
          {collection.description}
        </p>
      )}
      <div className="flex items-center gap-3 mt-3 text-xs text-surface-400">
        <span>{capabilityCount} capabilities</span>
        {collection.isPublic && (
          <span className="flex items-center gap-0.5">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            Public
          </span>
        )}
      </div>
    </div>
  </button>
);

// ─── CollectionsPage ──────────────────────────────────────────────────────────

const CollectionsPage: React.FC = () => {
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const collections = useWorkspaceStore(selectCollectionsList);
  const capabilities = useWorkspaceStore((s) => s.capabilities);
  const setCollections = useWorkspaceStore((s) => s.setCollections);
  const addToast = useUIStore((s) => s.addToast);

  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const workspaceId = activeWorkspace?.id ?? null;

  const { isLoading, error } = useQuery({
    queryKey: ['collections', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      const data = await collectionsApi.list(workspaceId);
      setCollections(data);
      await db.collections.bulkPut(data);
      return data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId || !newName.trim()) {
      return;
    }
    setIsCreating(true);
    try {
      const col = await collectionsApi.create(workspaceId, {
        name: newName.trim(),
        description: newDescription.trim() || null,
        workspaceId,
        iconEmoji: null,
        coverUrl: null,
        capabilityIds: [],
        parentId: null,
        isPublic: false,
      });
      useWorkspaceStore.getState().upsertCollection(col);
      setNewName('');
      setNewDescription('');
      setShowCreateForm(false);
      addToast({ title: 'Collection created', variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create';
      addToast({ title: 'Error', description: message, variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const filtered = searchQuery.trim()
    ? collections.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : collections;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-surface-900 dark:text-white">Collections</h1>
                <p className="text-sm text-surface-500 mt-0.5">Organise capabilities into curated sets</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New Collection
              </button>
            </div>

            {/* Create form */}
            {showCreateForm && (
              <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-card p-5">
                <h3 className="text-sm font-semibold text-surface-900 dark:text-white mb-4">
                  New collection
                </h3>
                <form onSubmit={handleCreate} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Collection name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    required
                    className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500"
                    autoFocus
                  />
                  <textarea
                    placeholder="Description (optional)"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2.5 outline-none focus:border-primary-500 resize-none"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-3 py-1.5 text-sm text-surface-600 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating || !newName.trim()}
                      className="px-4 py-1.5 text-sm font-medium text-white bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {isCreating ? 'Creating…' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search collections…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900"
              />
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-36 rounded-xl bg-surface-100 dark:bg-surface-800 animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-600 dark:text-red-400">
                Failed to load collections.
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 rounded-xl bg-surface-800 border border-surface-700 flex items-center justify-center mb-3 mx-auto">
                  <svg className="w-6 h-6 text-surface-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M2 8.5h20M2 8.5V6a2 2 0 0 1 2-2h4.5l2 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.5z"/></svg>
                </div>
                <p className="text-base font-medium text-surface-600 dark:text-surface-400">
                  {searchQuery ? 'No collections match your search' : 'No collections yet'}
                </p>
                {!searchQuery && (
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(true)}
                    className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Create your first collection
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filtered.map((collection) => (
                  <CollectionCard
                    key={collection.id}
                    collection={collection}
                    capabilityCount={
                      collection.capabilityIds.filter((id) => Boolean(capabilities[id])).length
                    }
                    onClick={() => history.push(`/collections/${collection.id}`)}
                  />
                ))}
              </div>
            )}
      </div>
    </AppShell>
  );
};

export default CollectionsPage;
